import { create } from 'zustand'
import type { UserProfile } from '@/types/auth'

// ─── Storage helpers ────────────────────────────────────────────
const STORAGE_KEY = 'ssma-auth'
const STORAGE_KEYS = {
	profile: 'ssma-profile-settings',
	tooltip: 'ssma-notices-tooltip-shown',
}

interface PersistedAuth {
	isAuthenticated: boolean
	user: UserProfile | null
}

function getStored(): PersistedAuth | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		return JSON.parse(raw) as PersistedAuth
	} catch {
		localStorage.removeItem(STORAGE_KEY)
		return null
	}
}

function persist(data: PersistedAuth) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
}

function clearStorage() {
	localStorage.removeItem(STORAGE_KEY)
	STORAGE_KEYS.profile && localStorage.removeItem(STORAGE_KEYS.profile)
	STORAGE_KEYS.tooltip && localStorage.removeItem(STORAGE_KEYS.tooltip)
}

// ─── Store ──────────────────────────────────────────────────────
const stored = getStored()

interface AuthState {
	user: UserProfile | null
	isAuthenticated: boolean

	/** Called after a successful login */
	login: (user: UserProfile) => void

	/** Update user profile in memory */
	updateUser: (user: UserProfile) => void

	/** Clear everything */
	logout: () => void
}

export const useAuthStore = create<AuthState>()((set) => ({
	user: stored?.user ?? null,
	isAuthenticated: stored?.isAuthenticated ?? false,

	login: (user) => {
		persist({ isAuthenticated: true, user })
		set({ user, isAuthenticated: true })
	},

	updateUser: (user) => {
		persist({ isAuthenticated: true, user })
		set({ user })
	},

	logout: () => {
		clearStorage()
		set({ user: null, isAuthenticated: false })
	},
}))
